import { cn } from '@/components/ui/utils';
import type { ChecklistItemWithEntry } from '../types';

interface Props {
  item: ChecklistItemWithEntry;
  onToggle: (templateItemId: string, checked: boolean) => void;
}

export function ChecklistItem({ item, onToggle }: Props) {
  const checked = item.entry?.checked ?? false;

  return (
    <div
      className={cn(
        'flex items-start gap-4 py-4 border-b border-[var(--muted)] last:border-0',
        'transition-colors duration-200',
        checked && 'bg-[var(--muted)]/30'
      )}
    >
      {/* Checkbox — padded hit area for accessibility */}
      <button
        role="checkbox"
        aria-checked={checked}
        aria-label={`Mark "${item.title}" as ${checked ? 'incomplete' : 'complete'}`}
        onClick={() => onToggle(item.id, !checked)}
        className={cn(
          'flex-shrink-0 w-5 h-5 rounded-[4px] border-2 mt-0.5',
          'transition-all duration-200 flex items-center justify-center',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-forest)]',
          'p-4 -m-4 cursor-pointer',  // enlarged hit area
          checked
            ? 'bg-[var(--color-forest)] border-[var(--color-forest)]'
            : 'bg-white border-[var(--color-forest)]'
        )}
        style={{ width: '20px', height: '20px', margin: '2px 0 0 0', padding: 0 }}
      >
        {checked && (
          <svg
            viewBox="0 0 12 10"
            fill="none"
            stroke="var(--color-warm-white)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-3 h-3"
            aria-hidden
          >
            <polyline points="1,5 4.5,8.5 11,1" />
          </svg>
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-4">
          <span
            className={cn(
              'text-[16px] font-[600] leading-snug transition-all duration-200',
              checked
                ? 'line-through text-[var(--color-mid-gray)]'
                : 'text-[var(--color-near-black)]'
            )}
          >
            {item.title}
          </span>

          {item.time_estimate_min && (
            <span className="text-[13px] text-[var(--color-mid-gray)] shrink-0">
              ~{item.time_estimate_min} min
            </span>
          )}
        </div>

        {item.description && (
          <p
            className={cn(
              'text-[14px] leading-relaxed mt-0.5 transition-colors duration-200',
              checked ? 'text-[var(--color-mid-gray)]' : 'text-[var(--color-charcoal)]'
            )}
          >
            {item.description}
          </p>
        )}

        {/* Source links */}
        {item.links && item.links.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {item.links.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-[4px]',
                  'text-[12px] font-[500] border transition-colors duration-200',
                  'bg-[var(--color-warm-white)] border-[var(--border)]',
                  'text-[var(--color-forest)] hover:bg-[var(--color-forest)] hover:text-[var(--color-warm-white)]'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                {link.label}
                <span aria-hidden className="opacity-60">↗</span>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
